import { createSelector } from "reselect"
import moment from "moment"
import { get, first, last, chain, sumBy, find } from "lodash"
import { ANALYZE_FACTORS_TAKE } from "helpers/analyzeConstants"
import { getStartDate, getEndDate, getSelectedDate } from "modules/security-profile/selectors/dates"
import { getModelFactors } from "selectors/modelMetadata"

const EMPTY_ARRAY = []
const getRiskRaw = state => get(state, "global.data.securityRiskHistory.data.model.simulation.risk", EMPTY_ARRAY)
const getCategory = state => get(state, "modules.securityProfileRisk.displayType")

function getFactorLabel(factors, id) {
  const factor = get(factors, id, { label: id })
  return factor.overviewLabel || factor.label
}

function getFactorCategory(factors, id) {
  return get(factors, [id, "category"])
}

export const getRisk = createSelector(
  [getRiskRaw],
  (risk) => {
    return chain(risk)
      .map(item => {
        if (item.total) {
          return {
            date: item.date,
            total: item.total,
            factorsTotal: item.attribution.summary.factors,
            specific: item.attribution.summary.specific,
            factors: chain(item.attribution.factors)
              .keyBy("id")
              .mapValues(f => f.value)
              .value(),
            categories: chain(item.attribution.factors)
              .groupBy("category")
              .mapValues(factors => sumBy(factors, "value"))
              .value(),
          }
        }

        return null
      })
      .filter(data => data)
      .value()
  },
)

export const getFilteredRisk = createSelector(
  [getRisk, getStartDate, getEndDate],
  (risk, startDate, endDate) =>
    risk.filter((data) => {
      if (data.date >= startDate && data.date <= endDate) {
        return data
      } else {
        return null
      }
    }),
)

export const getFirst = createSelector(
  [getRisk],
  (risk) => first(risk),
)

export const getCurrent = createSelector(
  [getRisk],
  (risk) => last(risk),
)

export const getFirstRiskDate = createSelector(
  [getFilteredRisk],
  (risk) => get(first(risk), "date", null),
)

const getSelectedDateFactors = createSelector(
  [getRiskRaw, getSelectedDate],
  (securityPerformanceHistory, date) => {
    const history = get(find(securityPerformanceHistory, d => moment(d.date).isSame(date)), "attribution.factors")
    return chain(history)
      .keyBy("id")
      .mapValues(item => ({ value: item.value }))
      .value()
  },
)

export const getRiskAttributionData = createSelector(
  [getSelectedDateFactors, getCategory, getModelFactors],
  (selectedDateFactors, category, factorsMetadata) => ({
    factors: chain(selectedDateFactors)
      .map((factor, id) => ({
        id,
        value: factor.value,
        label: getFactorLabel(factorsMetadata, id),
        category: getFactorCategory(factorsMetadata, id),
      }))
      .filter(factor =>
        !category || category === "total" || category === "factors" || category === factor.category,
      )
      .sortBy(factor => -Math.abs(factor.value))
      .take(ANALYZE_FACTORS_TAKE)
      .value(),
  }),
)

export const getMaxFactors = createSelector(
  [getRiskAttributionData],
  (riskAttribution) =>
    get(riskAttribution.factors, "[0].value", 0),
)
